import {React, _, observer, useMemo, useCallback, useState, useEffect, classNames, useContext, Fragment, Fuse, Highlighter} from '@api'
import {Button, HTMLSelect, Icon, Menu, MenuDivider, MenuItem, Popover, Classes, Spinner, ButtonGroup} from '@blueprintjs/core'
import {useStores, useVipLocalStore, VipLocalStore} from '@design-board/components'
import {SortByAlpha} from '@material-ui/icons'
import {OrderByField, ICatalogFieldConfig, IGroupOrderBy, ui, useCatalog, StoresInstance} from '@stores'
import {LabeledButton, MyPopover2, PrimaryIconButton, SearchField2} from 'centric-ui-components'
import {action, computed, observable, autorun, runInAction, toJS, trace} from 'mobx'
import {useLocalStore} from 'mobx-react'
import * as css from './CatalogSortByToolbarItems.css'
import SortIcon from '@material-ui/icons/Sort'
import {Popover2} from '@api'
import {SortAscendingIcon, SortDescendingIcon} from '@stores/ui'

export class SortMenuItemsStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
    this.catalog.componentStores.sort = this
  }

  registerEffects = () => {
    const toDispose = [
      autorun(
        () => {
          const {computedSortByGroup} = this,
            map = toJS(computedSortByGroup)

          runInAction(() => {
            this.sortByGroup.replace(map)
          })
        },
        {name: `Copy group by sort items into local cache`}),

      autorun(() => {
        const {sortByGroup} = this,
          touchAll = toJS(sortByGroup),
          map = sortByGroup.toJS()

        runInAction(() => {
          this.isDirty = !_.isEqual(map, this.computedSortByGroup)
          if (this.isDirty) {
            this._debouncedUpdateCatalogOrderBys()
          }
        })
      }, {name: `Update catalog card view after locally changing view options in dialog`})
    ]

    return () => toDispose.forEach(d => d())
  }

  /// region Search

  @observable searchText: string = ''
  onSearch = (e, value: string) => {
    this.searchText = value
  }

  @computed get isSearching() {
    return !_.isEmpty(this.searchText)
  }

  @computed get searchWords() {
    const {searchText} = this
    return searchText ? _.escapeRegExp(searchText).split(' ') : []
  }

  @computed get searchResults(): Set<string> {
    const {searchText, fieldSearcher} = this
    return new Set<string>(fieldSearcher && searchText !== '' ? fieldSearcher.search(searchText).map(v => v.item.field) : [])
  }

  @computed get fieldSearcher(): Fuse<{ field, label }> {
    const {catalog, groupBy} = this,
      {sortableFields, catalogItemOrderByFields} = catalog

    return new Fuse<{ field, label }>(
      [
        ...(catalogItemOrderByFields ?? []).map(f => ({field: f.field, label: catalog.labelForField(f, '', 'singular', true)})),
        ...(groupBy ? [] : [])
      ],
      {
        keys: ['label'],
        isCaseSensitive: false,
        shouldSort: false,
        includeScore: true,
        threshold: .2,
        ignoreLocation: true,
        findAllMatches: true,
        useExtendedSearch: true
      })
  }
  /// endregion

  @observable showingSortPopover = false
  @action onShowPopover = () => {
    this.catalog.hoverPopoverItemId = null
    this.showingSortPopover = true
  }

  @action onHidePopover = () => {
    this.showingSortPopover = false
    this.updateCatalogOrderBys()
  }

  _debouncedUpdateCatalogOrderBys = _.debounce(() => {
    this.updateCatalogOrderBys()
  }, 1500, {leading: false, trailing: true})

  updateCatalogOrderBys = () => {
    if (this.isDirty) {
      this.isDirty = false
      const {catalog} = this
      if (catalog.isProductView && catalog.groupBy.catalogItemViewStore) {
        catalog.groupBy.catalogItemViewStore.initialDataLoaded = false
      } else {
        // Causes full reload of catalog page, which is ugly vs reloading group contents
        //catalog.groupBy.viewStore.mgr.initialLoad = true
      }

      // Give a moment to turn off spinner
      setTimeout(() => {
        runInAction(() => {
          const {sortByGroup} = this,
            {groupBy: {groupingByName}} = this
          Array.from(sortByGroup.keys()).forEach(groupName => {
            groupingByName.get(groupName).orderBy.replace(sortByGroup.get(groupName))
          })
        })
      }, 50)
    }
  }

  @computed get computedSortByGroup() {
    const {groupBy: {catalogItemsGroup, hierarchyGroups}} = this,
      map = new Map<string, Array<IGroupOrderBy>>();

    [...hierarchyGroups, catalogItemsGroup].forEach(g => {
      g && map.set(g.groupName, _.cloneDeep(g.orderBy.slice()))
    })
    return map
  }

  @observable isDirty = false
  @observable sortByGroup = observable.map<string, Array<IGroupOrderBy>>()

  @computed get catalogItemOrderBy() {
    const {catalog: {groupBy}, sortByGroup} = this
    return sortByGroup.get(groupBy.catalogItemsGroup?.groupName) ?? []
  }

  @computed get catalogItemOrderByDict() {
    return _.keyBy(this.catalogItemOrderBy, f => f.field)
  }

  @action onClickSortMenu = (groupName, isProductView: boolean, field: string, custom?: boolean) => {
    const {sortByGroup, depth} = this,
      orderBy = sortByGroup.get(groupName)

    // Groups can only have a single sort field
    if (!isProductView) {
      orderBy.splice(0, orderBy.length)
    }

    // Are they unselecting a field?
    const index = orderBy.findIndex(f => f.field == field)
    if (index != -1) {
      if (orderBy.length === 1) {
        return
      }
      orderBy.splice(index, 1)
    } else {
      orderBy.push({field, custom: custom ?? true, order: 'asc'})
    }

    while (orderBy.length > depth) { // Delete off the beginning since we just pushed a value
      orderBy.splice(0, 1)
    }
  }

  @computed get depth() { return this.catalog.settings.sort.depth }

  onSetDepth = (depth: number) => e => {
    this.catalog.settings.sort.depth = depth

    const {orderBy} = this.catalog.groupBy.catalogItemsGroup
    while (orderBy.length > depth) { // Delete off the end
      orderBy.splice(1, 1)
    }
  }
}

export const SortMenuItemsStoreContext = React.createContext<SortMenuItemsStore>(undefined)
export const useSortMenuItemsStore = () => useContext(SortMenuItemsStoreContext)

export const CatalogSortByToolbarItems = observer(function CatalogSortByToolbarItems() {
  const store = useVipLocalStore((ws) => new SortMenuItemsStore(ws)),
    {
      onShowPopover, onHidePopover, showingSortPopover,
      strings, catalog: {_fieldByName, labelForField, groupBy: {isProductView, catalogItemsGroup}},
      stores: {
        sidebar: {disableToolbarMenus},
        workspaces: {workspaceConfig}
      },
      isSearching
    } = store

  useEffect(() => store.registerEffects(), [])

  return (
    <SortMenuItemsStoreContext.Provider value={store}>
      <div className={css.root}
          data-searching={isSearching}
      >
        <Popover2
          //interactionKind='hover'
          position={'bottom-right'}
          disabled={disableToolbarMenus}
          minimal
          className={isProductView && catalogItemsGroup ? 'labeled' : undefined}
          portalClassName={css.portal}
          onOpened={onShowPopover}
          onClosed={onHidePopover}
          content={<Menu>
            <SortMenuItems/>
          </Menu>}>
          <div className={css.labelAndDropdown}>
            {
              isProductView && catalogItemsGroup
              ? <LabeledButton
                label={strings.sortBy}
                showEllipsis
                rightIcon='caret-down'
                text={!workspaceConfig ? '...' : catalogItemsGroup.orderBy.map(({field, nestedField}) => labelForField(_fieldByName[field.replace(nestedField ? `.${nestedField}` : '', '')], field)).join(', ')}/>
              : <PrimaryIconButton tooltip={showingSortPopover ? '' : strings.sortBy} icon={<SortIcon cursor='pointer'/>}/>
            }
          </div>
        </Popover2>

        {/* Toggle asc/desc button */}
        {isProductView && catalogItemsGroup && catalogItemsGroup.orderBy.length == 1 &&
		 	 <PrimaryIconButton
           onClick={() => catalogItemsGroup.firstOrderBy.order = catalogItemsGroup.firstOrderBy.order === 'asc' ? 'desc' : 'asc'}
			     tooltip={`${catalogItemsGroup.firstOrderBy.order == 'asc' ? strings.ascending : strings.descending}`}
           icon={catalogItemsGroup.firstOrderBy.order == 'asc' ? <SortAscendingIcon/> : <SortDescendingIcon/>}/>
		 }
      </div>
    </SortMenuItemsStoreContext.Provider>
  )
})

/**
 * Sort Menu Item for some field in a group in our hierarchy (NOT a product field)
 */
export const GroupSortMenuItem = observer<{ field: string, label?: string, group: string }>(function GroupSortMenuItem({group, field, label}) {
  const {catalog: {groupBy: {groupingByName}}} = useStores(),
    hierarchyGroup = groupingByName.get(group),
    /** We usually sort by count or key but sometimes we sort by completely separate groups due to subtitle */
    sortByGroup = groupingByName.get(field),
    {fieldCustomSorts} = (sortByGroup || hierarchyGroup),
    hasCustomSort = fieldCustomSorts && fieldCustomSorts[sortByGroup ? '_key' : field],
    {firstOrderBy: orderBy} = hierarchyGroup

  //console.log({group, field, hierarchyGroup, sortByGroup, customSorts})

  return (
    <MenuItem
      className={css.sortMenuItem}
      text={(
        <div className={css.text}>
          <span className={css.sortLabel}>{label}</span>
          {hasCustomSort && <CustomSortIcons groupName={hierarchyGroup.groupName} isProductView={false} field={field} orderBy={hierarchyGroup.orderBy}/>}
        </div>
      )}
      // https://discuss.elastic.co/t/composite-aggregation-order-by/139563/6
      //disabled={subtitle?.length && field == '_count'}
      shouldDismissPopover={false}
      icon={orderBy.field == field ? 'tick' : 'blank'}
      onClick={() => {
        orderBy.field = field
        orderBy.custom = hasCustomSort != null
      }}/>
  )
})

const SortMenuDivider = observer<{ isProductView?: boolean, title: string, showSectionChooser?: boolean, showIcon?: boolean, orderBy: Array<{ order: 'asc' | 'desc', field: string }> }>(({isProductView, showSectionChooser, title, showIcon, orderBy}) => {
  const {isDirty, strings, updateCatalogOrderBys, depth, onSetDepth, onSearch, searchText} = useSortMenuItemsStore(),
    catalog = useCatalog()

  return <MenuDivider className={css.sortMenuDivider}
                      title={
                        <div className={css.title}>
                          <span className={css.text}>
                            {showSectionChooser ? <Fragment>
                              <BoardSectionSortDropdown/>
                              <div className={css.depthBar}>
                                <label>Depth:</label>
                                <ButtonGroup>
                                  {_.range(1, ui.settings.maxCatalogItemOrderByTerms + 1).map(g =>
                                    <Button text={g} minimal
                                            key={g}
                                            active={depth == g}
                                            onClick={onSetDepth(g)}
                                    />)}
                                </ButtonGroup>
                              </div>
                            </Fragment> : title}
                            {isDirty && <PrimaryIconButton
                                data-visible={isDirty}
                                tooltip={`Updating Catalog`}
                                className={css.dirtyIndicator} icon={<Spinner size={14}/>} onClick={updateCatalogOrderBys}/>
                            }
                          </span>
                          {showIcon != false &&
                           orderBy.map((ob, i) => {
                             const {field, order} = ob
                             return (
                                 <Button icon={order == 'asc' ? <SortAscendingIcon/> : <SortDescendingIcon/>}
                                         key={i}
                                         minimal
                                         title={order == 'asc' ? strings.sortAscending : strings.sortDescending}
                                         text={isProductView ? catalog.labelForFieldName_currentCatalog(field) : undefined}
                                         onClick={() => {
                                           ob.order = order == 'asc' ? 'desc' : 'asc'
                                         }}/>
                               )
                           })}

                          <SearchField2 className={css.search} active onSearch={onSearch} value={searchText}/>
                        </div>}/>
})

const SortMenuItemsForGroup = observer<{ group: string }>(function SortMenuItemsForGroup({group}) {
    const {catalog: {groupBy: {groupingByName}}} = useStores()

    if (groupingByName.get(group)) {
      const {firstOrderBy: orderBy, orderBy: allOrderBy, title, fieldCustomSorts, sortByFields: fields} = groupingByName.get(group)

      return (
        <Fragment>
          <SortMenuDivider title={`${title} Fields`} orderBy={allOrderBy}/>
          {(fields ?? []).map(({field, label}, i) => {
            return (
              <GroupSortMenuItem field={field} label={label} group={group} key={i}/>
            )
          })}
          {fieldCustomSorts && Object.keys(fieldCustomSorts).filter(k => k != '_key').map(k =>
            <MenuItem
              key={k}
              text={fieldCustomSorts[k]?.label ?? k}
              shouldDismissPopover={false}
              icon={(orderBy.field == k ? 'tick' : 'blank')}
              onClick={() => {
                orderBy.field = k
              }}
            />)}
        </Fragment>
      )
    }
    return null
  }
)

const CustomSortIcons = observer<{ groupName, isProductView, field, orderBy: Array<IGroupOrderBy> }>(function CustomSortIcons({groupName, isProductView, field, orderBy}) {
  const myOrderBy = orderBy.find(ob => ob.field == field),
    {strings, onClickSortMenu} = useSortMenuItemsStore()

  return <div className={css.buttons}>
      <PrimaryIconButton icon={groupName ? <SortByAlpha/> : <Icon icon={!myOrderBy || myOrderBy.order == 'asc' ? 'sort-alphabetical' : 'sort-alphabetical-desc'}/>}
                         active={myOrderBy && !myOrderBy.custom}
                         tooltip={strings.alphabetical}
                         onClick={action((e) => {
                           if (myOrderBy) {
                             myOrderBy.custom = false
                           } else {
                             onClickSortMenu(groupName, isProductView, field, false)
                           }
                           e.preventDefault()
                           e.stopPropagation()
                         })}/>
      <PrimaryIconButton icon={<Icon icon={groupName ? 'key' : !myOrderBy || myOrderBy?.order == 'asc' ? 'sort-asc' : 'sort-desc'}/>}
                         active={myOrderBy && myOrderBy.custom}
                         tooltip={strings.custom}
                         onClick={action((e) => {
                           if (myOrderBy) {
                             myOrderBy.custom = true
                           } else {
                             onClickSortMenu(groupName, isProductView, field, true)
                           }
                           e.preventDefault()
                           e.stopPropagation()
                         })}
      />
  </div>
})

/**
 * Top level component in popover shown for sort by menu
 */
export const SortMenuItems = observer(function SortMenuItems() {
  const {catalogItemOrderBy, isSearching, catalog, searchResults, strings, searchText} = useSortMenuItemsStore()

  if (!catalog.groupBy.hasLoaded) return null

  const {
    groupBy: {hierarchy}, catalogItemOrderByFields,
    workspaces: {workspaceConfig}
  } = catalog

  return (
    <div className={css.sortMenuItemContainer} data-searching={isSearching}>
      <SortMenuDivider title={`${workspaceConfig?.itemName ?? 'Product'} Fields`}
                       showIcon
                       isProductView
                       showSectionChooser={workspaceConfig?.sections?.filter(s => s?.sort?.catalogItem !== false)?.length > 0}
                       orderBy={catalogItemOrderBy}/>
      {isSearching && searchResults.size === 0
       ? <div className={css.noResults}>{strings.noResults} found for '{searchText}'</div>
       : <Fragment>
         <div className={css.catalogSortFields} style={{'--rows': catalogItemOrderByFields.length < 16 ? catalogItemOrderByFields.length : Math.ceil(catalogItemOrderByFields.length / (catalogItemOrderByFields.length < 32 ? 3 : 4))} as any}>
           {(catalogItemOrderByFields ?? []).map((item, i) => <SortMenuItemForField item={item} key={i}/>)}
         </div>

         {hierarchy.map(v => <SortMenuItemsForGroup key={v} group={v}/>)}
       </Fragment>}
    </div>
  )
})

const SortMenuItemForField = observer<{ item: OrderByField & { text: string } & Partial<ICatalogFieldConfig> }>(function SortMenuItemForField({item}) {
  const {groupBy: {allFieldsInCurrentHierarchy, catalogItemsGroup}, catalogItemOrderBy, catalogItemOrderByDict, onClickSortMenu,
    isSearching, searchResults, searchWords} = useSortMenuItemsStore(),
    selected = catalogItemOrderByDict[item.field],
    alreadyInHierarchy = allFieldsInCurrentHierarchy.some(f => f.field === item.field),
    onClick = useCallback(e => onClickSortMenu(catalogItemsGroup.groupName, true, item.field), [catalogItemsGroup, item])

  return (
    <li
      data-search-result={!isSearching ? undefined : searchResults.has(item.field)}
    >
    <a
      key={item.field}
      tabIndex={0}
      className={classNames(css.sortMenuItem, Classes.MENU_ITEM)}
      data-field={item.field}
      data-already-in-hierarchy={alreadyInHierarchy}
      onClick={alreadyInHierarchy ? undefined : onClick}>
      <Icon icon={selected
                  ? catalogItemOrderBy.length == 1 ? 'tick' : <span className={classNames(Classes.ICON, css.orderNumber)}>{catalogItemOrderBy.indexOf(selected) + 1}</span>
                  : 'blank'}/>
      <div className={Classes.FILL}>
        <div className={css.text}>
          <Highlighter className={css.sortLabel} textToHighlight={item.text} searchWords={searchWords}/>
          {!alreadyInHierarchy && item.sort?.custom && <CustomSortIcons groupName={catalogItemsGroup?.groupName} isProductView field={item.field} orderBy={catalogItemOrderBy}/>}
        </div>
      </div>
    </a>
    </li>)
})

export const BoardSectionSortDropdown = observer<{ hidden? }>(function BoardSectionSortDropdown() {
  const catalog = useCatalog(),
    {strings, workspaceConfig: {sections, title, _id}, sortBySection: section} = catalog,
    options = useMemo(() => _.uniq([strings.all, ...sections.filter(s => s?.sort?.catalogItem !== false).map(s => s.label)]), [sections, title]),
    onChange = useCallback(({currentTarget: {value}}) => {
      catalog.sortBySection = value == strings.all ? '' : sections.find(f => f.label == value).id
    }, [title, sections])

  // console.log({options, section})
  return <HTMLSelect
    className={css.boardSelect}
    minimal
    options={options}
    onChange={onChange}
    value={!section ? strings.all : (sections.find(f => f.id == section)?.label ?? '...')}/>
})



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/topBar/components/CatalogSortByToolbarItems.tsx